import { lazy, Suspense, useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, Input, Modal } from "@shop-ledger/ui-components";
import { useAuth } from "../auth/AuthContext";
import { lookupInventoryItem } from "../api/inventoryApi";
import type { InventoryItemOutExt } from "../api/types";
import { ApiError } from "../lib/apiClient";
import { topUpStock } from "../lib/topUpStock";
import { SupplierSelect } from "./SupplierSelect";
import { StatusBanner } from "./StatusBanner";

const CameraScanner = lazy(() =>
  import("../hardware/scanner/CameraScanner").then((m) => ({ default: m.CameraScanner })),
);

export interface ScanToAddStockModalProps {
  open: boolean;
  onClose: () => void;
  onToppedUp: () => void;
}

type Phase = "scanning" | "looking-up" | "confirming";

/**
 * "Scan to add stock" — the business-owner ask this was built for: restocking many items
 * by hand is faster scan-first than hunt-through-the-table-first. Scans (camera or typed,
 * §4.2's manual-entry fallback) resolve via the same `GET /inventory/lookup?code=`
 * (`api/inventoryApi.ts::lookupInventoryItem`) the till's online barcode tier uses — this
 * app has no offline mode, so there's no local-cache tier to replicate here, just the one
 * network call. Once resolved, the quantity/cost/supplier step is the exact same
 * create-PO-then-receive-it sequence `TopUpStockModal` uses (`lib/topUpStock.ts`, shared
 * rather than duplicated), and on success loops back to scanning immediately — the point
 * is working through a stack of boxes one barcode at a time without re-opening a modal
 * per item.
 */
export function ScanToAddStockModal({ open, onClose, onToppedUp }: ScanToAddStockModalProps) {
  const { ensureFreshToken } = useAuth();
  const [phase, setPhase] = useState<Phase>("scanning");
  const [manualCode, setManualCode] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [resolvedItem, setResolvedItem] = useState<InventoryItemOutExt | null>(null);

  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState("0.00");
  const [supplierId, setSupplierId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastAdded, setLastAdded] = useState<string | null>(null);

  function resetForNextScan() {
    setPhase("scanning");
    setManualCode("");
    setLookupError(null);
    setResolvedItem(null);
    setQuantity("");
    setUnitCost("0.00");
    setSupplierId("");
    setBatchNumber("");
    setExpiryDate("");
    setSubmitError(null);
  }

  function handleClose() {
    resetForNextScan();
    setLastAdded(null);
    onClose();
  }

  async function handleCode(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;
    setLookupError(null);
    setPhase("looking-up");
    try {
      const token = await ensureFreshToken();
      const item = await lookupInventoryItem(token, trimmed);
      setResolvedItem(item);
      setUnitCost(item.wholesale_price ?? "0.00");
      setPhase("confirming");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setLookupError(`No product found for "${trimmed}".`);
      } else {
        setLookupError(err instanceof ApiError ? err.message : "Lookup failed — check connectivity and try again.");
      }
      setPhase("scanning");
    }
  }

  function handleManualSubmit(event: FormEvent) {
    event.preventDefault();
    void handleCode(manualCode);
  }

  async function handleAddStock(event: FormEvent) {
    event.preventDefault();
    if (!resolvedItem) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await topUpStock(token, {
        branchId: resolvedItem.branch_id,
        itemId: resolvedItem.id,
        quantity: asDecimalString(quantity),
        unitCost: asDecimalString(unitCost),
        supplierId,
        batchNumber,
        expiryDate,
      });
      setLastAdded(`Added ${quantity} × ${resolvedItem.name}.`);
      onToppedUp();
      resetForNextScan();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError(
          "This tenant requires approval before new purchase orders are received. The order was created but stock hasn't " +
            "increased yet — find it under Purchase Orders once it's approved.",
        );
      } else {
        setSubmitError(err instanceof ApiError ? err.message : "Could not add stock.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title="Scan to add stock">
      {lastAdded ? <StatusBanner kind="info">{lastAdded}</StatusBanner> : null}

      {phase === "confirming" && resolvedItem ? (
        <form onSubmit={handleAddStock}>
          <p className="muted">
            {resolvedItem.name} <span className="muted">({resolvedItem.sku})</span> — current stock {resolvedItem.current_stock}
          </p>
          <div className="form-grid">
            <Input label="Quantity to add" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} required autoFocus />
            <Input label="Unit cost (optional)" inputMode="decimal" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} />
            <SupplierSelect id="scan-topup-supplier" label="Supplier (optional)" value={supplierId} onChange={setSupplierId} />
            <Input
              label="Batch number (optional)"
              value={batchNumber}
              onChange={(e) => setBatchNumber(e.target.value)}
              placeholder="e.g. LOT-2026-07"
            />
            <Input label="Expiry date (optional)" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
          </div>
          {submitError ? <StatusBanner kind="error">{submitError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Adding stock…" : "Add stock and scan next"}
            </Button>
            <Button type="button" variant="ghost" onClick={resetForNextScan}>
              Rescan (wrong item)
            </Button>
            <Button type="button" variant="ghost" onClick={handleClose}>
              Done
            </Button>
          </div>
        </form>
      ) : (
        <div className="sl-scan-to-stock">
          <Suspense fallback={<p className="muted">Loading camera…</p>}>
            <CameraScanner onScan={(code) => void handleCode(code)} onClose={handleClose} />
          </Suspense>
          <form onSubmit={handleManualSubmit} className="sl-scan-to-stock__manual">
            {/* autoFocus doubles as physical-HID-scanner support: a hardware barcode
                scanner is keyboard emulation, so it only "just works" if this field is
                already focused — no separate capture code needed, unlike the till's
                useHidScanner (which exists because the till has no single input to focus).
                Remounts (this branch replacing the "confirming" form, or the modal
                reopening) re-trigger autoFocus, so a scanner works immediately at the
                start of every scan-next cycle, not just the first one. */}
            <Input
              label="Or type SKU / barcode"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="e.g. TOMATO-1KG"
              autoComplete="off"
              autoFocus
            />
            <Button type="submit" variant="secondary" disabled={phase === "looking-up"}>
              {phase === "looking-up" ? "Looking up…" : "Look up"}
            </Button>
          </form>
          {lookupError ? <StatusBanner kind="error">{lookupError}</StatusBanner> : null}
        </div>
      )}
    </Modal>
  );
}
